import { useState, useEffect, useCallback, useRef } from 'react';
import { Task } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { 
  subscribeToUserTasks, 
  createTask as createTaskInFirestore, 
  updateTask as updateTaskInFirestore, 
  deleteTask as deleteTaskInFirestore 
} from '@/services/taskService';
import { scheduleReminderNotification, cancelReminderNotification } from '@/services/notificationService';

export function useTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const previousTasksRef = useRef<Task[]>([]);

  // Subscribe to real-time task updates
  useEffect(() => {
    if (!user) {
      setTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    
    const unsubscribe = subscribeToUserTasks(
      user.id,
      (updatedTasks) => {
        setTasks(updatedTasks);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading tasks:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Handle notification scheduling when tasks change (from Firestore sync)
  useEffect(() => {
    const previousTasks = previousTasksRef.current;
    
    // Find new or updated tasks
    tasks.forEach(async (task) => {
      const previousTask = previousTasks.find(t => t.id === task.id);
      
      // New task synced from Firestore
      if (!previousTask && task.type === 'reminder' && !task.completed) {
        const dueDate = new Date(task.dueDate);
        if (dueDate > new Date()) {
          await scheduleReminderNotification(task);
        }
      }
      // Task updated (check if due date or completion status changed)
      else if (previousTask && task.type === 'reminder') {
        const dueDateChanged = new Date(previousTask.dueDate).getTime() !== new Date(task.dueDate).getTime();
        const completionChanged = previousTask.completed !== task.completed;
        
        if (dueDateChanged || completionChanged) {
          await cancelReminderNotification(task.id);
          
          if (!task.completed && new Date(task.dueDate) > new Date()) {
            await scheduleReminderNotification(task);
          }
        }
      }
    });
    
    // Find deleted tasks and cancel their notifications
    previousTasks.forEach(async (previousTask) => {
      const stillExists = tasks.find(t => t.id === previousTask.id);
      if (!stillExists && previousTask.type === 'reminder') {
        await cancelReminderNotification(previousTask.id);
      }
    });
    
    // Update reference
    previousTasksRef.current = tasks;
  }, [tasks]);

  const addTask = useCallback(async (task: Omit<Task, 'id'>) => {
    try {
      if (!user) throw new Error('User not authenticated');
      
      // Create task in Firestore
      const taskId = await createTaskInFirestore({
        ...task,
        // createdBy MUST be the dsaUserId — Firestore security rule ownershipValid() requires it
        // and subscribeToUserTasks queries on this field
        createdBy: user.id,
        // Keep a denormalized display name for UI rendering without extra lookups
        createdByName: task.createdByName || user?.name || user.id,
      });
      
      // Note: Notification will be scheduled by the useEffect above when task syncs back
      
      return taskId;
    } catch (err) {
      console.error('Error adding task:', err);
      throw err;
    }
  }, [user]);

  const updateTask = useCallback(async (taskId: string, updates: Partial<Task>) => {
    try {
      // Update task in Firestore
      await updateTaskInFirestore(taskId, updates);
      
      // Note: Notification will be rescheduled by the useEffect above when update syncs back
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  }, []);

  const deleteTask = useCallback(async (taskId: string) => {
    try {
      // Delete task from Firestore
      await deleteTaskInFirestore(taskId);
      
      // Note: Notification will be cancelled by the useEffect above when deletion syncs back
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  }, []);

  return { tasks, loading, error, addTask, updateTask, deleteTask };
}
